import { GetStaticProps } from 'next';

import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetPermitsOverview } from 'api/graphql-helper';
import { PageInformationFieldsFragment, PermitsOverviewSingleFieldsFragment } from 'api/graphql-types';

import { Head } from 'modules/head/head';
import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import IntroHeader from 'modules/intro-header/intro-header';

import { initialState as settingsInitialState } from 'redux/stores/settings';
import { initialState as permitsInitialState } from 'redux/stores/permits';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IBasePageProps } from 'types/page';
import { ROUTES } from 'constants/routes';
import { searchPermits } from 'api/algolia';
import { PERMIT_OVERVIEW_LIMIT } from 'modules/permits-overview/permits-overview';

interface IPermitsOverviewPageProps extends IBasePageProps {
	permitsOverview?: PermitsOverviewSingleFieldsFragment;
	pageInformation?: PageInformationFieldsFragment;
}

const PermitsOverviewPage = (props: IPermitsOverviewPageProps): React.JSX.Element => {
	const modules = props.permitsOverview?.modules || [];
	const seoModule = props?.permitsOverview?.seo;

	return (
		<Layout navbar={props?.pageInformation?.navbar} footer={props?.pageInformation?.footer}>
			<Head seoModule={seoModule} seoParent={props?.pageInformation?.seo} />
			<IntroHeader module={props?.permitsOverview?.header} slug={ROUTES.PERMITS.fullPath} />
			<ModuleBuilder modules={modules} slug={ROUTES.PERMITS.fullPath} />
		</Layout>
	);
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const result = await getServerPageGetPermitsOverview({ variables: { locale, publicationState } }, client);
		const {
			pageInformation,
			permitsOverview,
			permitLaws_connection: permitLaws,
			permitProductTypes_connection: permitProductTypes,
			themes_connection: themes
		} = result.props.data;

		const { results: permits, total: permitsCount } = await searchPermits(
			'',
			{
				themesIds: [],
				permitLawsIds: [],
				permitProductTypesIds: []
			},
			{
				sort: 'asc',
				page: 1,
				limit: PERMIT_OVERVIEW_LIMIT,
				locale
			}
		);

		if (permitsOverview == null) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				pageInformation: pageInformation?.data?.attributes,
				permitsOverview: permitsOverview?.data?.attributes,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'permits-overview',
						contentTypeId: permitsOverview?.data?.documentId
					},
					permits: {
						...permitsInitialState,
						permits,
						permitsCount,
						permitLaws:
							permitLaws?.data?.map((item) => ({
								id: item.documentId,
								locale,
								name: item?.attributes?.name
							})) || [],
						permitLawCount: permitLaws?.pageInfo.total || 0,
						permitProductTypes:
							permitProductTypes?.data?.map((item) => ({
								id: item.documentId,
								locale,
								name: item?.attributes?.name
							})) || [],
						permitProductTypeCount: permitProductTypes?.pageInfo.total || 0
					},
					themes: {
						themes: themes?.data || [],
						totalThemes: themes?.pageInfo.total || 0
					}
				}
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in permits overview', error);

		return {
			notFound: true
		};
	}
};
export default PermitsOverviewPage;
